import React from 'react';
import { Navigation } from '@/components/navigation';
import { Footer } from '@/components/footer';
import { ServiceDetailPage } from '@/components/service-detail-page';
import { SEO } from '@/components/seo';

const customSoftwareService = {
  name: "Custom Software",
  promise: "Tailored solutions that solve your specific business challenges",
  primaryKPI: "3x faster than off-the-shelf alternatives for unique workflows",
  signals: {
    bestFor: "Companies with unique workflows that generic software can't handle",
    timeline: "8-16 weeks depending on complexity",
    team: "Tech lead + 2-3 full-stack developers + QA engineer",
    deliverables: "Custom application + source code + infrastructure"
  },
  outputs: [
    "Custom application architecture tailored to your needs",
    "Full source code ownership and documentation",
    "Cloud infrastructure setup and deployment",
    "Comprehensive API documentation",
    "User training and onboarding materials",
    "Ongoing maintenance and support options"
  ],
  process: [
    {
      step: "Requirements & Architecture",
      description: "Deep dive into your business needs and design scalable architecture",
      ownership: "We lead discovery, you provide business requirements",
      artifacts: ["Requirements document", "System architecture", "Technical specifications"]
    },
    {
      step: "Agile Development Sprints",
      description: "Build in 2-week increments with continuous feedback",
      ownership: "We develop, you review and approve sprints",
      artifacts: ["Working software", "Sprint reports", "Demo recordings"]
    },
    {
      step: "Testing & Quality Assurance",
      description: "Rigorous testing for reliability, security, and performance",
      ownership: "We test and fix, you perform UAT",
      artifacts: ["Test reports", "Security audit", "Performance benchmarks"]
    },
    {
      step: "Deployment & Handoff",
      description: "Launch with full documentation and knowledge transfer",
      ownership: "We deploy and train, you take ownership",
      artifacts: ["Production deployment", "Documentation", "Training materials"]
    }
  ],
  proof: [
    {
      client: "Logistics company",
      challenge: "Inefficient routing wasted fuel and time",
      result: "Custom routing software optimized delivery schedules",
      metric: "32% reduction in fuel costs"
    },
    {
      client: "Education platform",
      challenge: "Off-the-shelf LMS didn't match their pedagogy",
      result: "Tailored learning management system built from scratch",
      metric: "78% increase in student engagement"
    },
    {
      client: "Real estate firm",
      challenge: "Manual property management processes were bottlenecks",
      result: "Custom system automated workflows and reporting",
      metric: "50% reduction in admin time"
    }
  ],
  faqs: [
    {
      question: "Do we own the source code?",
      answer: "Absolutely. You own 100% of the code we write for you. We provide full source code, documentation, and intellectual property rights. You're never locked into our services and can maintain or extend the software with your own team."
    },
    {
      question: "Can the software scale as we grow?",
      answer: "Yes. We design all custom software with scalability in mind, using cloud-native architectures, microservices where appropriate, and modern best practices. We plan for 10x growth from day one so you're never constrained."
    },
    {
      question: "What happens after the software is built?",
      answer: "You have options: maintain it yourself with our documentation, contract us for ongoing support, or train your team to take over. We offer flexible support packages including bug fixes, feature additions, and technical consulting."
    },
    {
      question: "How do you ensure security?",
      answer: "Security is built in from the start. We follow OWASP guidelines, implement authentication and authorization, encrypt sensitive data, conduct security audits, and follow industry best practices. We can also achieve compliance certifications if needed."
    },
    {
      question: "Can you integrate with our existing tools?",
      answer: "Yes. Integration is often a key requirement. We've connected custom software with CRMs, ERPs, payment gateways, accounting systems, and countless other tools via APIs, webhooks, database connections, and custom middleware."
    }
  ]
};

const CustomSoftware = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Custom Software Development - Tailored Enterprise Solutions | AffinityAim"
        description="Custom software development for unique business needs. CRM, ERP, workflow automation. 8-16 week delivery with dedicated team."
        canonical="https://www.affinityaim.com/services/custom-software"
        keywords="custom software development, enterprise software, bespoke applications, CRM development"
        type="service"
      />
      <Navigation />
      <main className="pt-32">
        <ServiceDetailPage service={customSoftwareService} />
      </main>
      <Footer />
    </div>
  );
};

export default CustomSoftware;
